// client/src/context/ConfirmContext.jsx
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import ConfirmModal from '../components/common/ConfirmModal';
import ToastContainer from '../components/common/ToastContainer';

const ConfirmContext = createContext(null);

export const ConfirmProvider = ({ children }) => {
  // Estado para modal de confirmación (Promise-based)
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirmar',
    cancelText: 'Cancelar',
    variant: 'danger',
    icon: null,
  });
  const confirmPromiseRef = useRef(null);

  const confirm = useCallback((options) => {
    return new Promise((resolve) => {
      confirmPromiseRef.current = resolve;
      const opts = typeof options === 'string' ? { message: options } : options || {};
      setConfirmState({
        isOpen: true,
        title: opts.title || '¿Está seguro?',
        message: opts.message || '',
        confirmText: opts.confirmText || 'Confirmar',
        cancelText: opts.cancelText !== undefined ? opts.cancelText : 'Cancelar',
        variant: opts.variant || 'danger',
        icon: opts.icon || null,
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setConfirmState((prev) => ({ ...prev, isOpen: false }));
    if (confirmPromiseRef.current) {
      confirmPromiseRef.current(true);
      confirmPromiseRef.current = null;
    }
  }, []);

  const handleCancel = useCallback(() => {
    setConfirmState((prev) => ({ ...prev, isOpen: false }));
    if (confirmPromiseRef.current) {
      confirmPromiseRef.current(false);
      confirmPromiseRef.current = null;
    }
  }, []);

  // Estado para modal de alerta bloqueante
  const [alertState, setAlertState] = useState({
    isOpen: false,
    title: '',
    message: '',
    buttonText: 'Entendido',
    variant: 'info',
  });
  const alertPromiseRef = useRef(null);

  const alertModal = useCallback((options) => {
    return new Promise((resolve) => {
      alertPromiseRef.current = resolve;
      const opts = typeof options === 'string' ? { message: options } : options || {};
      setAlertState({
        isOpen: true,
        title: opts.title || 'Notificación del Sistema',
        message: opts.message || '',
        buttonText: opts.buttonText || 'Entendido',
        variant: opts.variant || 'info',
      });
    });
  }, []);

  const handleAlertClose = useCallback(() => {
    setAlertState((prev) => ({ ...prev, isOpen: false }));
    if (alertPromiseRef.current) {
      alertPromiseRef.current();
      alertPromiseRef.current = null;
    }
  }, []);

  // Sistema de Toasts flotantes
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'info', duration = 3500) => {
    const id = Date.now() + Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev, { id, message, type }]);
    if (duration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, duration);
    }
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const notify = {
    success: (msg, dur) => addToast(msg, 'success', dur),
    error: (msg, dur) => addToast(msg, 'error', dur),
    warning: (msg, dur) => addToast(msg, 'warning', dur),
    info: (msg, dur) => addToast(msg, 'info', dur),
  };

  // Interceptor de seguridad por si alguna llamada residual externa invoca window.alert
  useEffect(() => {
    const originalAlert = window.alert;
    window.alert = (msg) => {
      addToast(String(msg), 'info');
    };
    return () => {
      window.alert = originalAlert;
    };
  }, [addToast]);

  return (
    <ConfirmContext.Provider value={{ confirm, alert: alertModal, notify }}>
      {children}

      {/* Modal de confirmación (2 botones) */}
      <ConfirmModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        variant={confirmState.variant}
        icon={confirmState.icon}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />

      {/* Modal de alerta (1 botón) */}
      <ConfirmModal
        isOpen={alertState.isOpen}
        title={alertState.title}
        message={alertState.message}
        confirmText={alertState.buttonText}
        cancelText={null}
        variant={alertState.variant}
        onConfirm={handleAlertClose}
        onCancel={handleAlertClose}
      />

      {/* Contenedor de notificaciones toast */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm debe ser utilizado dentro de un ConfirmProvider');
  }
  return context;
};

export default ConfirmContext;
